import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, catchError, map } from 'rxjs';
import { environment } from '../environments/environment';
import { ContaBancaria } from '../models/contaBancaria.model';
import { CacheService } from './cache.service';

@Injectable({
  providedIn: 'root'
})
export class ContaBancariaService {
  private apiUrl = `${environment.advContabilApi}/api/ContaBancaria`;
  private brasilApiUrl = 'https://brasilapi.com.br/api/banks/v1';

  constructor(
    private http: HttpClient,
    private cacheService: CacheService
  ) { }

  adicionarContaBancaria(contaBancaria: Partial<ContaBancaria>): Observable<ContaBancaria> {
    return this.http.post<ContaBancaria>(`${this.apiUrl}/inserir`, contaBancaria, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    });
  }

  obterContaBancaria(id: string | number): Observable<ContaBancaria> {
    return this.http.get<ContaBancaria>(`${this.apiUrl}/obter/${id}`);
  }

  updateContaBancaria(id: number, contaBancaria: ContaBancaria): Observable<ContaBancaria> {
    return this.http.put<ContaBancaria>(`${this.apiUrl}/atualizar/${id}`, contaBancaria);
  }

  listaContasBancarias(userId: string): Observable<ContaBancaria[]> {
    return this.cacheService.getOrSetContasBancarias(userId, () => {
      return this.http.get<ContaBancaria[]>(`${this.apiUrl}/listar?usuarioId=${userId}`);
    });
  }
  
  deleteContaBancaria(id: any): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/deletar/${id}`);
  }

  obterBancos(): Observable<any[]> {
    return this.http.get<any[]>(this.brasilApiUrl);
  }

  obterBancoPorCodigo(codigo: string): Observable<any> {
    return this.cacheService.getOrSetBanco(codigo, () => {
      return this.http.get(`${this.brasilApiUrl}/${codigo}`).pipe(
        catchError(error => {
          console.warn(`Erro ao buscar banco ${codigo}:`, error);
          return of({
            name: `Banco ${codigo}`,
            code: codigo,
            ispb: '00000000'
          });
        })
      );
    });
  }

  obterBancosPorCodigos(codigos: string[]): Observable<Map<string, any>> {
    const bancosUnicos = [...new Set(codigos)];
    const observables = bancosUnicos.map(codigo => 
      this.obterBancoPorCodigo(codigo).pipe(
        map(banco => ({ codigo, banco }))
      )
    );

    return new Observable(observer => {
      if (observables.length === 0) {
        observer.next(new Map());
        observer.complete();
        return;
      }

      Promise.all(observables.map(obs => obs.toPromise()))
        .then(results => {
          const bancoMap = new Map<string, any>();
          results.forEach(result => {
            if (result) {
              bancoMap.set(result.codigo, result.banco);
            }
          });
          observer.next(bancoMap);
          observer.complete();
        })
        .catch(error => {
          observer.error(error);
        });
    });
  }

  limparCache(): void {
    this.cacheService.invalidatePattern('banco_');
    this.cacheService.invalidatePattern('contas_bancarias_');
  }

  invalidarCacheUsuario(userId: string): void {
    this.cacheService.delete(`contas_bancarias_${userId}`);
  }
}
